/**
 * Plain-thunk client for the agent-conversation list/get/update/delete
 * endpoints exposed by axilon-api-service at `/api/ontology/agent-conversations/*`.
 *
 * The streaming orchestrator runs in `run-agent-orchestrator.ts` (consumeSSE);
 * these helpers cover the non-streaming side (rail list, transcript load,
 * rename, delete) without taking on RTK Query's machinery — the api-service is on a
 * different base URL than the proxy-backed RTK slices (which go to the proxy
 * directly), so a plain thunk here is simpler than threading absolute URLs
 * through RTK.
 *
 * Multi-tab note (AXI-3530): these thunks no longer focus the loaded /
 * created conversation. They dispatch `openTab` (idempotent — opens the tab
 * if missing, seeds turns / attachments) but never `setActiveTab`. The
 * controller hook decides whether to focus based on where the user is —
 * e.g. `createAgentConversation` is composed with `setActiveTab` when the
 * pre-create path fires from the pending new-chat state, but the post-stream
 * refetch in `run-agent-orchestrator` deliberately omits it so a user who
 * switched tabs mid-stream isn't yanked back when their original tab
 * finishes.
 */

import { createOntologyHeaders, PROXY_API_V1_URL } from '../api/core'
import { getOntologyServiceBaseUrl } from '../config'
import {
  openTab,
  prependAgentOrchestratorConversation,
  removeAgentOrchestratorConversation,
  setActiveTab,
  setAgentOrchestratorConversations,
  setTabAttachments,
  updateAgentOrchestratorConversationTitle,
  type ConversationAttachment,
  type ConversationRun,
  type ConversationSummary,
  type ConversationWithRuns,
  type RunStatus
} from '../slices/agent-orchestrator-slice'
import type { AppDispatch, RootState } from '../store'
import { hydratePendingCypherWrites } from './cypher-write-pending'
import { dispatchUnresumedAgentActionPlans } from './cypher-write-resume'
import { removeChatComposerModeForConversation } from './orchestrator-ui-persistence'

/**
 * Conversation title cap mirrors the api-service's auto-derive cap so the
 * pre-create path produces the same title the server would have written.
 */
const TITLE_FROM_INSTRUCTION_MAX_LENGTH = 80

/**
 * Manual rename cap. Matches proxy `MaxTitleLength`. Auto-derived titles
 * from the first instruction stay at `TITLE_FROM_INSTRUCTION_MAX_LENGTH`.
 */
export const CONVERSATION_TITLE_MAX_LENGTH = 200

/** Derive a readable conversation title from the user's first instruction. */
export function deriveConversationTitle(instruction: string): string {
  const trimmed = instruction.trim()
  if (trimmed.length <= TITLE_FROM_INSTRUCTION_MAX_LENGTH) return trimmed
  return trimmed.slice(0, TITLE_FROM_INSTRUCTION_MAX_LENGTH - 3) + '...'
}

function endpoint(path = ''): string {
  return `${getOntologyServiceBaseUrl()}/agent-conversations${path}`
}

/**
 * `createOntologyHeaders()` always sets `Content-Type: application/json`,
 * but body-less requests (GET, DELETE) shouldn't carry that header — Fastify
 * with `bodyLimit > 0` invokes its JSON parser whenever Content-Type says
 * `application/json` regardless of method, and rejects with
 * `FST_ERR_CTP_EMPTY_JSON_BODY` on an empty body. Strip it for body-less
 * requests.
 */
function createBodylessHeaders(): Headers {
  const headers = createOntologyHeaders()
  headers.delete('Content-Type')
  return headers
}

export class AgentConversationRequestError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message)
    this.name = 'AgentConversationRequestError'
  }
}

/**
 * The proxy serializes a run's lifecycle field as `state`; the slice's
 * `ConversationRun` interface (and every past-turn consumer — e.g. the
 * chat's running-turn filter and the aborted-turn error suppression)
 * reads `status`. Stamp `status` from the wire field at the fetch
 * boundary so server-loaded turns carry the field the rest of the app
 * expects. Recursive — child subagent runs share the wire shape.
 */
export function normalizeConversationRun(run: ConversationRun): ConversationRun {
  const wireState = (run as ConversationRun & { state?: RunStatus }).state
  return {
    ...run,
    status: run.status ?? wireState,
    children: Array.isArray(run.children) ? run.children.map(normalizeConversationRun) : []
  }
}

async function jsonFetch<T>(url: string, init: RequestInit, parseEmpty: false): Promise<T>
async function jsonFetch<T>(url: string, init: RequestInit, parseEmpty: true): Promise<T | null>
async function jsonFetch<T>(
  url: string,
  init: RequestInit,
  parseEmpty: boolean
): Promise<T | null> {
  const response = await fetch(url, init)
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new AgentConversationRequestError(
      response.status,
      `${init.method ?? 'GET'} ${url} failed with HTTP ${response.status}${text ? `: ${text}` : ''}`
    )
  }
  const text = await response.text().catch(() => '')
  if (!text.trim()) {
    if (parseEmpty) return null
    throw new Error(`Expected JSON body from ${init.method ?? 'GET'} ${url}`)
  }
  return JSON.parse(text) as T
}

/**
 * GET /api/ontology/agent-conversations — fetch the list rail, sorted by
 * the proxy as `updated_at DESC`, and write into the slice.
 *
 * Uses the proxy's AIP-158 pagination envelope
 * (`{conversations, next_page_token}`); the rail currently only consumes
 * the first page. `next_page_token` is dropped on the floor — wire it
 * through the slice if/when we add an "Older" pager.
 */
export function loadAgentConversations(opts?: { pageSize?: number; pageToken?: string }) {
  return async (dispatch: AppDispatch): Promise<ConversationSummary[]> => {
    const params = new URLSearchParams()
    if (opts?.pageSize !== undefined) params.set('page_size', String(opts.pageSize))
    if (opts?.pageToken !== undefined && opts.pageToken !== '') {
      params.set('page_token', opts.pageToken)
    }
    const q = params.toString()
    const url = q ? `${endpoint()}?${q}` : endpoint()
    const page = await jsonFetch<{
      conversations: ConversationSummary[]
      next_page_token?: string
    }>(url, { method: 'GET', headers: createBodylessHeaders() }, false)
    dispatch(setAgentOrchestratorConversations(page.conversations))
    return page.conversations
  }
}

/**
 * POST /api/ontology/agent-conversations — pre-create a conversation row
 * BEFORE the orchestrator SSE stream opens. The browser uses this on the
 * very first submit of a fresh conversation so the rail surfaces the new
 * conversation immediately (and the user can navigate away from a
 * still-streaming run).
 *
 * Returns the created `ConversationSummary` (with `turn_count: 0` derived
 * client-side — the proxy returns the header without it). Also dispatches:
 *   - `prependAgentOrchestratorConversation(summary)` so the rail updates.
 *   - `openTab({ id, turns: [] })` so the tab slot exists for subsequent
 *     `setActiveTab` / `startTabRun` dispatches.
 *
 * Does NOT call `setActiveTab` — the caller composes that when it wants
 * the new conversation focused. The orchestrator thunk's pre-create path
 * does so immediately after this resolves.
 */
export function createAgentConversation(opts: {
  instruction: string
  /**
   * Conversation origin forwarded to the proxy. Omit for the default
   * orchestrator Chat rail. Pass `'ingest_query'` for a per-job Ask lane so
   * the conversation stays out of GET /agent-conversations (origin filter).
   */
  origin?: 'orchestrator' | 'ingest_query'
  /**
   * Display-only title source. When set, the conversation title is derived
   * from this string instead of `instruction` (used for attachment-only
   * sends so the filename is not copied into the model prompt).
   */
  titleSeed?: string
}) {
  return async (dispatch: AppDispatch): Promise<ConversationSummary> => {
    const title = deriveConversationTitle(opts.titleSeed ?? opts.instruction)
    const origin = opts.origin
    // `createOntologyHeaders()` already sets Content-Type: application/json
    // and X-API-Key. Pass the `Headers` object directly so fetch handles the
    // case-insensitive header names correctly — flattening to a plain
    // record breaks the Content-Type and Fastify rejects with 415.
    const created = await jsonFetch<{
      id: string
      title: string
      accessScopeId?: string
      canContribute?: boolean
      created_at: string
      updated_at: string
      origin?: string
    }>(
      endpoint(),
      {
        method: 'POST',
        headers: createOntologyHeaders(),
        body: JSON.stringify({
          title,
          ...(origin !== undefined ? { origin } : {})
        })
      },
      false
    )
    const summary: ConversationSummary = {
      ...created,
      canContribute: true,
      turn_count: 0,
      ...(origin !== undefined ? { origin } : {})
    }
    // Ingest-query conversations are job-scoped Ask lanes — keep them out of
    // the Chat history rail (proxy list already filters by origin, but the
    // optimistic prepend would still flash them into the strip).
    if (origin !== 'ingest_query') {
      dispatch(prependAgentOrchestratorConversation(summary))
    }
    dispatch(
      openTab({
        id: summary.id,
        turns: [],
        canContribute: true,
        hidden: origin === 'ingest_query'
      })
    )
    return summary
  }
}

/**
 * GET /api/ontology/agent-conversations/:id — load one conversation header
 * + nested run tree and ensure a tab exists for it. The proxy returns
 * top-level orchestrator runs as `runs[]` (each carrying its messages +
 * child subagent runs); the slice surfaces them as `tab.turns` since one
 * orchestrator run = one user-facing "turn".
 *
 * Idempotent via `openTab` — calling this for an already-open tab just
 * refreshes its turns. Does NOT change focus; the caller composes
 * `setActiveTab` when it wants the loaded conversation focused. This lets
 * the post-stream refetch in `run-agent-orchestrator` update a tab the
 * user has since switched away from without yanking them back.
 */
export function loadActiveAgentConversation(id: string, options?: { hidden?: boolean }) {
  return async (dispatch: AppDispatch): Promise<ConversationWithRuns> => {
    const fetched = await jsonFetch<ConversationWithRuns>(
      endpoint(`/${encodeURIComponent(id)}`),
      { method: 'GET', headers: createBodylessHeaders() },
      false
    )
    const conv: ConversationWithRuns = {
      ...fetched,
      runs: (fetched.runs ?? []).map(normalizeConversationRun)
    }
    dispatch(
      openTab({
        id: conv.id,
        turns: conv.runs,
        canContribute: conv.canContribute === true,
        hidden: options?.hidden
      })
    )
    try {
      await dispatch(hydratePendingCypherWrites(conv.id))
    } catch (hydrateErr) {
      console.warn('[agent-conversations] pending cypher-write hydrate failed', hydrateErr)
    }
    // Settled-but-unresumed plans: rows where the user clicked Apply /
    // Reject in a previous browser session but the synthetic follow-up
    // agent turn never fired. Claim each (the proxy's
    // `resume_dispatched_at` guarantees only one tab wins) and dispatch
    // the resume turn for the winners.
    //
    // Skipped while a run is still open server-side: firing a resume
    // turn then would start a second concurrent run on this conversation
    // (the same collision the mid-stream deferral in
    // `maybeFireAgentActionPlanResume` exists to prevent). The plan is
    // picked up once the run settles — by the post-stream
    // `fireSettledAgentActionPlans`, or by the refetch that follows the
    // re-attached `/resume` stream finishing.
    const hasRunningRun = conv.runs.some((r) => r.status === 'running')
    if (conv.canContribute === true && !hasRunningRun) {
      void dispatch(dispatchUnresumedAgentActionPlans(conv.id))
    }
    return conv
  }
}

/**
 * Copy a settled transcript into a fresh caller-owned conversation and focus it.
 * `throughRunId` keeps that top-level turn and everything before it.
 * Drafts, staged attachments, queues, and grants are intentionally not copied.
 * A full copy sends no body: Fastify rejects an empty JSON body.
 */
export function copyAgentConversation(id: string, options?: { throughRunId?: string }) {
  return async (dispatch: AppDispatch): Promise<ConversationWithRuns> => {
    const throughRunId = options?.throughRunId?.trim()
    const fetched = await jsonFetch<ConversationWithRuns>(
      endpoint(`/${encodeURIComponent(id)}/copy`),
      throughRunId
        ? {
            method: 'POST',
            headers: createOntologyHeaders(),
            body: JSON.stringify({ through_run_id: throughRunId })
          }
        : { method: 'POST', headers: createBodylessHeaders() },
      false
    )
    const copied: ConversationWithRuns = {
      ...fetched,
      canContribute: true,
      runs: (fetched.runs ?? []).map(normalizeConversationRun)
    }
    const summary: ConversationSummary = {
      id: copied.id,
      title: copied.title,
      accessScopeId: copied.accessScopeId,
      canContribute: true,
      turn_count: copied.runs.length,
      created_at: copied.created_at,
      updated_at: copied.updated_at,
      ...(copied.origin !== undefined ? { origin: copied.origin } : {})
    }
    dispatch(prependAgentOrchestratorConversation(summary))
    dispatch(
      openTab({
        id: copied.id,
        turns: copied.runs,
        attachments: [],
        canContribute: true
      })
    )
    dispatch(setActiveTab({ id: copied.id }))
    return copied
  }
}

/**
 * DELETE /api/ontology/agent-conversations/:id — drop the row (cascades
 * turns) and remove it from the slice's list. The caller is responsible for
 * also closing any tab open on this conversation and cancelling any
 * in-flight run.
 */
export function deleteAgentConversation(id: string) {
  return async (dispatch: AppDispatch): Promise<void> => {
    await jsonFetch<null>(
      endpoint(`/${encodeURIComponent(id)}`),
      { method: 'DELETE', headers: createBodylessHeaders() },
      true
    )
    dispatch(removeAgentOrchestratorConversation({ id }))
    removeChatComposerModeForConversation(id)
  }
}

/**
 * PATCH /api/ontology/agent-conversations/:id — rewrite the display title.
 * Updates the rail in place so recency groups do not jump. Reverts the
 * optimistic title if the proxy rejects the write.
 */
export function renameAgentConversation(id: string, title: string) {
  return async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const nextTitle = title.trim()
    if (nextTitle.length === 0) return
    const previousTitle = getState().agentOrchestrator.conversations.find(
      (conversation) => conversation.id === id
    )?.title
    dispatch(updateAgentOrchestratorConversationTitle({ id, title: nextTitle }))
    try {
      const updated = await jsonFetch<{ title: string }>(
        endpoint(`/${encodeURIComponent(id)}`),
        {
          method: 'PATCH',
          headers: createOntologyHeaders(),
          body: JSON.stringify({ title: nextTitle })
        },
        false
      )
      if (updated.title !== nextTitle) {
        dispatch(updateAgentOrchestratorConversationTitle({ id, title: updated.title }))
      }
    } catch (err) {
      if (previousTitle !== undefined) {
        dispatch(updateAgentOrchestratorConversationTitle({ id, title: previousTitle }))
      }
      throw err
    }
  }
}

/**
 * GET /api/ontology/agent-conversations/:id/attachments — fetch every
 * file the user has uploaded across the lifetime of a conversation. The
 * api-service forwards the proxy's `{attachments: AttachmentSummary[]}`
 * envelope unchanged; we drop the resolved list into the tab via
 * `setTabAttachments`, which silently no-ops when the tab no longer exists
 * (e.g. closed mid-fetch).
 *
 * Returns `[]` (and dispatches an empty list) when the proxy has no
 * attachment routes registered (S3 disabled). Errors are propagated to
 * the caller — the chat sidebar surfaces them inline so the user knows
 * the documents panel is stale.
 */
export function loadActiveAgentConversationAttachments(id: string) {
  return async (dispatch: AppDispatch): Promise<ConversationAttachment[]> => {
    const body = await jsonFetch<{ attachments: ConversationAttachment[] }>(
      endpoint(`/${encodeURIComponent(id)}/attachments`),
      { method: 'GET', headers: createBodylessHeaders() },
      false
    )
    const attachments = Array.isArray(body.attachments) ? body.attachments : []
    dispatch(setTabAttachments({ id, attachments }))
    return attachments
  }
}

/** Fetch one persisted conversation attachment for preview or download. */
export async function fetchAgentConversationAttachment(
  conversationId: string,
  attachmentId: string
): Promise<{ blob: Blob; contentType: string }> {
  const response = await fetch(
    `${PROXY_API_V1_URL}/agent-conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}/download`,
    { headers: createBodylessHeaders() }
  )
  if (!response.ok) {
    const text = await response.text().catch(() => '')
    throw new Error(
      `conversation attachment fetch failed (${response.status})${text ? `: ${text}` : ''}`
    )
  }
  return {
    blob: await response.blob(),
    contentType: response.headers.get('Content-Type')?.split(';')[0]?.trim() || ''
  }
}
